import type { Message } from '@nexo/types';
import { EmptyState, Spinner } from '@nexo/ui';
import { useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { Paginated } from '@nexo/types';
import { MessageSquareText } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { keys, useThread } from '../../lib/queries';
import { Composer } from './Composer';
import { MessageItem } from './MessageItem';
import { buildTimeline } from './timeline';
import { useTranslation } from 'react-i18next';

/** The latest copy of a message: from the channel timeline cache, or fetched on its own. */
function useMessage(messageId: string) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: keys.message(messageId),
    queryFn: () => api.messages.get(messageId),
    initialData: () => {
      // ['channel', …] also holds pins and files (plain arrays): only look at timelines.
      for (const [, data] of queryClient.getQueriesData({ queryKey: ['channel'] })) {
        if (!data || typeof data !== 'object' || !('pages' in data)) continue;
        const found = (data as InfiniteData<Paginated<Message>>).pages
          .flatMap((p) => p.items)
          .find((m) => m.id === messageId);
        if (found) return found;
      }
      return undefined;
    },
  });
}

/** A thread in the right panel: the original message, its replies and a reply box. */
export function ThreadPanel() {
  const { threadId, channels } = useTeam();
  const { t } = useTranslation();
  if (!threadId) {
    return (
      <EmptyState
        icon={<MessageSquareText className="size-6" />}
        title={t('Ningún hilo abierto')}
        description={t('Pasa el ratón por un mensaje y pulsa «Responder en hilo».')}
      />
    );
  }
  return <Thread key={threadId} threadId={threadId} channels={channels} />;
}

function Thread({
  threadId,
  channels,
}: {
  threadId: string;
  channels: ReturnType<typeof useTeam>['channels'];
}) {
  const parent = useMessage(threadId);
  const replies = useThread(threadId);
  const { t } = useTranslation();
  const bottom = useRef<HTMLDivElement>(null);
  const channel = channels.find((c) => c.id === parent.data?.channelId);
  const count = replies.data?.length ?? 0;

  // Follow new replies.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [count]);

  if (parent.isPending || replies.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center text-muted">
        <Spinner />
      </div>
    );
  }
  if (!parent.data || !channel) {
    return (
      <EmptyState title={t('Hilo no disponible')} description={t('Puede que se haya borrado.')} />
    );
  }
  const timeline = buildTimeline(replies.data ?? []);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto pb-2 [&_article]:px-4">
        <MessageItem message={parent.data} compact={false} inThread />
        <div className="my-2 flex items-center gap-3 px-4 text-xs text-subtle">
          {count === 1 ? t('1 respuesta') : t('{{count}} respuestas', { count })}
          <span className="h-px flex-1 bg-border" />
        </div>
        {timeline.map((item) =>
          item.type === 'day' ? null : (
            <MessageItem key={item.key} message={item.message} compact={item.compact} inThread />
          ),
        )}
        <div ref={bottom} />
      </div>
      <div className="px-3 pb-3">
        <Composer channel={channel} parentId={threadId} />
      </div>
    </div>
  );
}
